import { GoogleMap, MarkerF, useJsApiLoader, PolylineF } from '@react-google-maps/api'
import { GOOGLE_MAPS_KEY } from '../config/keys'
import { CUSTOM_MAP_STYLE } from '../constants/mapStyle'
import type { History, Vehicle } from '../types'
import { getFullPath } from '../helpers/path'
import './FleetMap.css'

const defaultCenter = { lat: 34.0539293, lng: -6.7928056 }

type Props = {
  vehicles?: Vehicle[]
  focus?: History | null
  path?: History[]
  height?: string | number
}

export function FleetMap({ vehicles = [], focus, path = [], height = '100%' }: Props) {
  const { isLoaded } = useJsApiLoader({
    id: 'driveops-maps',
    googleMapsApiKey: GOOGLE_MAPS_KEY,
  })

  const center = focus
    ? { lat: focus.latitude, lng: focus.longitude }
    : vehicles.find((v) => v.latestPosition)?.latestPosition
      ? {
          lat: vehicles.find((v) => v.latestPosition)!.latestPosition!.latitude,
          lng: vehicles.find((v) => v.latestPosition)!.latestPosition!.longitude,
        }
      : defaultCenter

  const poly = path
    .filter((p) => Number.isFinite(p.latitude) && Number.isFinite(p.longitude))
    .map((p) => ({ lat: p.latitude, lng: p.longitude }))

  if (!isLoaded) {
    return <div className="fleet-map fleet-map--loading" style={{ height }} />
  }

  return (
    <div className="fleet-map" style={{ height }}>
      <GoogleMap
        mapContainerStyle={{ width: '100%', height: '100%' }}
        center={center}
        zoom={path.length > 1 ? 12 : 13}
        options={{
          styles: CUSTOM_MAP_STYLE,
          disableDefaultUI: true,
          zoomControl: true,
        }}
      >
        {vehicles.map((v) => {
          const lp = v.latestPosition
          if (!lp || !Number.isFinite(lp.latitude) || !Number.isFinite(lp.longitude)) return null
          const iconUrl = getFullPath('types', v.typeId?.icon)
          return (
            <MarkerF
              key={v._id}
              position={{ lat: lp.latitude, lng: lp.longitude }}
              title={v.name}
              icon={
                iconUrl
                  ? {
                      url: iconUrl,
                      scaledSize: new google.maps.Size(36, 36),
                    }
                  : undefined
              }
            />
          )
        })}
        {focus && Number.isFinite(focus.latitude) && (
          <MarkerF position={{ lat: focus.latitude, lng: focus.longitude }} />
        )}
        {poly.length > 1 && (
          <PolylineF
            path={poly}
            options={{ strokeColor: '#4576E5', strokeOpacity: 0.9, strokeWeight: 4 }}
          />
        )}
      </GoogleMap>
    </div>
  )
}
